import { CAMPAIGNS_MARKETING } from 'constants/Entities';
import { getLiteral, getLiteralWithParameters } from 'utils/getLiteral';

const HISTORY_LABEL_ENTITY_MAP = {
    marketingCampaign: CAMPAIGNS_MARKETING,
};

/**
 * B2 parameterized i18n label from marketing status history API fields.
 * @typedef {object} MarketingStatusHistoryLabel
 * @property {string} Key Literal key.
 * @property {Array<string|number|MarketingStatusHistoryLabel>} [Values] Placeholder values.
 * @property {Array<{ Id: number, Entity: string }>} [Refs] Linked entities for placeholder values.
 */

/**
 * @typedef {object} ParsedMarketingStatusHistoryLabel
 * @property {string|null} key
 * @property {string[]} values
 * @property {Array<{ id: number, entity: import('constants/Entities').EntityConfig|null }>} refs
 */

/**
 * Maps B2 history label ref entity strings to app entity constants.
 * @param {string|null|undefined} refEntity
 */
export function resolveHistoryLabelEntity(refEntity) {
    if (refEntity == null || refEntity === '') {
        return null;
    }
    return HISTORY_LABEL_ENTITY_MAP[String(refEntity)] ?? null;
}

/**
 * Normalizes a history label value from the API into key, values, and refs.
 * @param {string | MarketingStatusHistoryLabel | null | undefined} value
 * @returns {ParsedMarketingStatusHistoryLabel|null}
 */
export function parseMarketingStatusHistoryLabel(value) {
    const coalesced = coalesceLabelValue(value);

    if (coalesced == null || coalesced === '') {
        return null;
    }

    if (typeof coalesced === 'string') {
        return {
            key: coalesced,
            values: [],
            refs: [],
        };
    }

    if (typeof coalesced === 'object') {
        const key = coalesced.Key ?? coalesced.key ?? null;
        const rawValues = coalesced.Values ?? coalesced.values;
        const rawRefs = coalesced.Refs ?? coalesced.refs;

        return {
            key,
            values: normalizeLabelValues(rawValues),
            refs: normalizeRefs(rawRefs),
        };
    }

    return null;
}

/**
 * Resolves a history timeline label as plain text (no links).
 * @param {string | MarketingStatusHistoryLabel | null | undefined} value
 * @returns {string}
 */
export function resolveMarketingStatusHistoryLabel(value) {
    const parsed = parseMarketingStatusHistoryLabel(value);

    if (!parsed?.key) {
        return '';
    }

    if (!parsed.values.length) {
        return getLiteral(parsed.key);
    }

    return getLiteralWithParameters(parsed.key, parsed.values);
}

function coalesceLabelValue(value) {
    if (value == null || value === '') {
        return null;
    }

    if (typeof value === 'object') {
        return value;
    }

    if (typeof value !== 'string') {
        return value;
    }

    const trimmed = value.trim();
    if (!trimmed.startsWith('{')) {
        return trimmed;
    }

    try {
        const parsed = JSON.parse(trimmed);
        if (parsed && typeof parsed === 'object') {
            return parsed;
        }
    } catch {
        // Plain literal key that happens to start with "{" is unlikely; fall through.
    }

    return trimmed;
}

function normalizeRefs(refs) {
    if (!Array.isArray(refs)) {
        return [];
    }

    return refs
        .map((ref) => {
            const rawId = ref?.Id ?? ref?.id;
            const parsedId = Number(rawId);
            if (!Number.isFinite(parsedId) || parsedId <= 0) {
                return null;
            }

            return {
                id: parsedId,
                entity: resolveHistoryLabelEntity(ref?.Entity ?? ref?.entity),
            };
        })
        .filter(Boolean);
}

function normalizeLabelValues(values) {
    if (!Array.isArray(values)) {
        return [];
    }

    return values
        .filter((item) => item != null && item !== '')
        .map((item) => {
            if (typeof item === 'object') {
                return resolveMarketingStatusHistoryLabel(item);
            }
            return String(item);
        })
        .filter((item) => item !== '');
}
